import type { ReactElement } from 'react';
import type { BeendeteAbstimmung, Ergebnis } from '@mcikm-vote/shared';

import { Ergebnisbalken } from '../../../components/Ergebnisbalken';
import { Karte } from '../../../components/Karte';
import { Kennzahlen } from '../../../components/Kennzahlen';
import { formatiereZeitpunkt } from '../../../formatierung';

/**
 * Ergebnisansicht (§18.1, §18.2, §19.2).
 *
 * Zeigt Abstimmungsdaten, Beteiligungszahlen, absolute und prozentuale Werte sowie horizontale
 * Balken. Sie erscheint ausschließlich, wenn der Administrator das Ergebnis veröffentlicht hat
 * (§18.3) — vorher liefert der Server es nicht.
 *
 * Die Ergebnisfeststellung nach §18.4 folgt in S4.
 */
export function ErgebnisAnsicht({
  abstimmung,
  ergebnis,
}: {
  abstimmung: BeendeteAbstimmung;
  ergebnis: Ergebnis;
}): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <Karte>
        <p className="text-text-gedaempft text-sm font-medium">
          Ergebnis der Abstimmung {abstimmung.nummer ?? '—'}
        </p>
        <h2 className="mt-1 text-xl font-semibold">{abstimmung.ueberschrift}</h2>

        <div className="mt-4">
          <Kennzahlen
            zahlen={[
              {
                beschriftung: 'Berücksichtigte Teilnehmer',
                wert: ergebnis.beruecksichtigteTeilnehmer,
              },
              { beschriftung: 'Abgegebene Stimmen', wert: ergebnis.abgegebeneStimmen },
              { beschriftung: 'Nicht abgegeben', wert: ergebnis.nichtAbgegebeneStimmen },
            ]}
          />
        </div>
      </Karte>

      <Karte>
        <h3 className="mb-4 text-base font-semibold">Stimmenverteilung</h3>

        <Ergebnisbalken
          optionen={ergebnis.optionen}
          abgegebeneStimmen={ergebnis.abgegebeneStimmen}
        />

        <p className="text-text-gedaempft mt-4 text-xs">
          Prozentwerte bezogen auf die abgegebenen Stimmzettel, gerundet auf eine Nachkommastelle.
          {ergebnis.mehrfachnennungenMoeglich &&
            ' Mehrfachnennungen waren zulässig; die Summe kann deshalb über 100 Prozent liegen.'}
        </p>
      </Karte>

      {/* §18.4, §19.2: gegebenenfalls die Ergebnisfeststellung — sie verändert keine Zahlen. */}
      {ergebnis.feststellung !== null && (
        <Karte>
          <h3 className="text-base font-semibold">Feststellung</h3>
          <p className="mt-2 whitespace-pre-line">{ergebnis.feststellung}</p>
        </Karte>
      )}

      {ergebnis.berichtigungen.length > 0 && (
        <Karte>
          <h3 className="text-base font-semibold">Berichtigungen</h3>
          <ul className="mt-2 flex flex-col gap-2">
            {ergebnis.berichtigungen.map((eintrag) => (
              <li key={eintrag.erstelltAm} className="text-sm">
                <span className="text-text-gedaempft block text-xs">
                  {formatiereZeitpunkt(eintrag.erstelltAm)}
                </span>
                {eintrag.text}
              </li>
            ))}
          </ul>
        </Karte>
      )}
    </div>
  );
}
